import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Heart, Sparkles, Award, ArrowRight } from "lucide-react";
import { api } from "@/api/client";
import { useI18n } from "@/i18n";
import { vhrpImages } from "@/lib/site";
import { Button } from "@/components/ui/button";

export default function About() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [assets, setAssets] = useState({});

  useEffect(() => {
    api("/app-assets").then(setAssets).catch(() => {});
  }, []);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const remote = (key, fallback) => {
    const u = assets[key];
    if (!u) return fallback;
    return u.startsWith("http") ? u : `${backend}${u}`;
  };

  const objectives = [t("obj1"), t("obj2"), t("obj3"), t("obj4"), t("obj5"), t("obj6")];

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden bg-[#29211D]">
        <img src={vhrpImages.aboutHero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(41,33,29,0.85), rgba(191,54,12,0.7))" }} />
        <div className="relative mx-auto max-w-5xl px-4 py-20 text-center">
          <span className="text-xs font-black uppercase tracking-widest text-brand-tint">{t("aboutKicker")}</span>
          <h1 className="mt-3 text-3xl font-black text-white sm:text-5xl">{t("aboutTitle")}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">{t("aboutHeroSub")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-14">
        {/* Story */}
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <img src={vhrpImages.mission} alt="" className="h-72 w-full rounded-3xl object-cover shadow-lg" />
          <div>
            <h2 className="text-2xl font-extrabold text-ink">{t("aboutStoryTitle")}</h2>
            <p className="mt-3 leading-relaxed text-ink-soft">{t("aboutStory")}</p>
          </div>
        </div>

        {/* Objectives */}
        <h2 className="mt-16 text-2xl font-extrabold text-ink">{t("objectivesTitle")}</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {objectives.map((o, i) => (
            <div key={o} className="rounded-2xl border border-line bg-white p-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tint font-black text-brand">{i + 1}</div>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">{o}</p>
            </div>
          ))}
        </div>

        {/* Purpose / Values / Initiatives / Achievements */}
        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          <InfoCard icon={Target} img={remote("about_purpose", vhrpImages.values1)} title={t("purposeTitle")} body={t("purposeBody")} />
          <InfoCard icon={Heart} img={remote("about_values", vhrpImages.values2)} title={t("coreValuesTitle")} body={t("coreValuesBody")} />
          <InfoCard icon={Sparkles} img={vhrpImages.mission} title={t("initiativesTitle")} body={t("initiativesBody")} />
          <InfoCard icon={Award} img={vhrpImages.aboutHero} title={t("achievementsTitle")} body={t("achievementsBody")} />
        </div>

        {/* Founder */}
        <div className="mt-16 overflow-hidden rounded-3xl bg-[#29211D] lg:grid lg:grid-cols-[280px_1fr]">
          <img src={remote("about_founder", vhrpImages.founder)} alt={t("founderName")} className="h-72 w-full object-cover lg:h-full" />
          <div className="p-8">
            <div className="text-xs font-black uppercase tracking-widest text-brand-tint">{t("founderSecTitle")}</div>
            <div className="mt-2 text-2xl font-extrabold text-white">{t("founderName")}</div>
            <div className="text-sm text-white/70">{t("founderRole")}</div>
            <p className="mt-4 leading-relaxed text-white/85">{t("founderDesc")}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col items-center gap-4 rounded-3xl bg-brand-tint p-10 text-center">
          <h3 className="text-2xl font-extrabold text-brand-dark">{t("joinCta")}</h3>
          <Button data-testid="about-join-cta" onClick={() => navigate("/membership")} className="h-12 rounded-full bg-brand px-8 font-bold hover:bg-brand-dark">
            {t("heroCta")} <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, img, title, body }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-line bg-white">
      <img src={img} alt="" className="h-44 w-full object-cover" />
      <div className="p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tint text-brand"><Icon size={18} /></span>
          <h3 className="text-lg font-bold text-ink">{title}</h3>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink-soft">{body}</p>
      </div>
    </div>
  );
}
